import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import "./ServiceDetail.css";
import { services, caseStudies, process } from '../data/site';

const ServiceDetail = () => {
  const { id } = useParams();
  const service = services.find((s) => s.id === id);

  if (!service) return <Navigate to="/services" replace />;

  const relatedCases = caseStudies.slice(0, 3);
  const otherServices = services.filter((s) => s.id !== id).slice(0, 6);

  return (
    <div className="service-detail-page page-shell">
      <section className="sd-hero section-dark">
        <div className="site-container-wide sd-hero-grid">
          <div>
            <Link to="/services" className="sd-back">← All services</Link>
            <span className="section-eyebrow">Service · {service.icon}</span>
            <h1>{service.title}</h1>
            <p>{service.short}</p>
            <div className="hero-actions">
              <Link to="/request-demo" className="btn-primary-large">Book consultation</Link>
              <Link to="/case-studies" className="btn-ghost btn-arrow">Related outcomes</Link>
            </div>
          </div>
          <div className="hero-video-frame">
            <div className="frame-glow" />
            <img src="/images/hero-ai-brain.svg" alt={service.title} />
            <span className="play-pill">DALYTIX PRACTICE</span>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="site-container-wide sd-content-grid">
          <article className="sd-content">
            <span className="section-eyebrow">Practice overview</span>
            <h2>How we approach {service.title.toLowerCase()}.</h2>
            <p>{service.description}</p>

            <h3>What we do</h3>
            <ul className="sd-list">
              {service.capabilities.map((c) => (<li key={c}>{c}</li>))}
            </ul>

            <h3>What you get</h3>
            <div className="sd-deliverables">
              {service.deliverables.map((d) => (
                <div className="sd-deliverable" key={d}>
                  <span>✓</span>
                  <p>{d}</p>
                </div>
              ))}
            </div>

            <h3>How we deliver</h3>
            <p>
              Every Dalytix engagement runs the same six-stage delivery model — adapted to the practice
              context but always anchored to a measurable outcome and a clean handover.
            </p>
            <div className="sd-process">
              {process.map((p) => (
                <div className="sd-process-item" key={p.step}>
                  <strong>{p.step}</strong>
                  <span>{p.title}</span>
                </div>
              ))}
            </div>
          </article>

          <aside className="sd-aside">
            <div className="sd-card">
              <h3>Get a tailored briefing</h3>
              <p>Share your goal and we will send a tailored {service.icon} practice briefing within 48 hours.</p>
              <Link to="/contact" className="btn-primary">Request briefing</Link>
            </div>
            <div className="sd-card">
              <h4>Recent recognitions</h4>
              <ul>
                <li>Forrester Wave Leader 2025</li>
                <li>Gartner Visionary 2025</li>
                <li>Databricks PoY 2024 &amp; 2025</li>
                <li>AWS Premier Tier 2025</li>
              </ul>
            </div>
            <div className="sd-card">
              <h4>Related practices</h4>
              <ul className="sd-related">
                {otherServices.map((s) => (
                  <li key={s.id}>
                    <Link to={`/services/${s.id}`}>{s.title} →</Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="section sd-related-cases section-dark">
        <div className="site-container-wide">
          <div className="section-head-row">
            <div>
              <span className="section-eyebrow">Outcomes</span>
              <h2 className="section-title">Where this practice has shipped.</h2>
            </div>
            <Link to="/case-studies" className="btn-ghost btn-arrow">All case studies</Link>
          </div>
          <div className="sd-cases-grid">
            {relatedCases.map((cs) => (
              <Link to={`/case-studies/${cs.id}`} className="sd-case-card glass-card" key={cs.id}>
                <img src={cs.image} alt={cs.title} />
                <span className="eyebrow-tag">{cs.industry}</span>
                <h3>{cs.title}</h3>
                <strong>{cs.metric}</strong>
                <span className="sd-case-meta">{cs.metricLabel}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
